import React, { useState, useEffect, useCallback, useRef } from 'react';
import DnaGoDnAiWidget from './DnaGoDnAiWidget';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/lib/api';
import { 
  Search, Users, Clock, AlertTriangle, CheckCircle, 
  Bell, ArrowRight, Loader2, UserCheck, Phone, Eye,
  RefreshCw, Filter, Volume2, Plus, Sparkles, Brain,
  TrendingUp, Zap, ChevronRight, Camera, Shield, MessageCircle
} from 'lucide-react';
import { WhatsAppButton } from './DnaGoNotifications';

const STATUS_CONFIG = {
  aguardando: { color: 'bg-amber-400', borderColor: 'border-amber-500', label: 'Aguardando', icon: Clock, textColor: 'text-amber-700' },
  chamado: { color: 'bg-blue-500', borderColor: 'border-blue-600', label: 'Chamado', icon: Bell, textColor: 'text-blue-700' },
  deslocamento: { color: 'bg-purple-500', borderColor: 'border-purple-600', label: 'Em Deslocamento', icon: ArrowRight, textColor: 'text-purple-700' },
  retirado: { color: 'bg-green-500', borderColor: 'border-green-600', label: 'Retirado', icon: CheckCircle, textColor: 'text-green-700' },
  alerta: { color: 'bg-red-500', borderColor: 'border-red-600', label: 'Alerta', icon: AlertTriangle, textColor: 'text-red-700' },
};

export default function DnaGoPainelSaida() {
  const [fila, setFila] = useState([]);
  const [turmas, setTurmas] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [autorizados, setAutorizados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroTurma, setFiltroTurma] = useState('todas');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, aluno: null });
  const [selectAutorizadoDialog, setSelectAutorizadoDialog] = useState({ open: false, aluno: null, autorizados: [] });
  const [addAlunoDialog, setAddAlunoDialog] = useState(false);
  const [selectedAutorizado, setSelectedAutorizado] = useState(null);
  const [searchAluno, setSearchAluno] = useState('');
  const [dashboard, setDashboard] = useState({
    retiradas_hoje: 0,
    fila_atual: 0,
    tempo_medio_minutos: 0,
    ocorrencias_abertas: 0
  });
  
  // IA Insights
  const [iaInsights, setIaInsights] = useState([]);
  const [showIA, setShowIA] = useState(true);
  
  // Auto-refresh
  const refreshInterval = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        api.get('/dnago/fila'),
        api.get('/class-groups'),
        api.get('/students'),
        api.get('/dnago/autorizados?ativo=true'),
        api.get('/dnago/dashboard')
      ]);
      
      const [filaRes, turmasRes, alunosRes, autRes, dashRes] = results;
      
      const filaData = filaRes.status === 'fulfilled' ? (filaRes.value.data || []) : [];
      const turmasData = turmasRes.status === 'fulfilled' ? (turmasRes.value.data || []) : [];
      const alunosData = alunosRes.status === 'fulfilled' ? (alunosRes.value.data || []) : [];
      const autData = autRes.status === 'fulfilled' ? (autRes.value.data || []) : [];
      const dashData = dashRes.status === 'fulfilled' ? (dashRes.value.data || {}) : {};
      
      // Ordenar fila por prioridade IA
      const filaOrdenada = calcularPrioridadeIA(filaData);
      setFila(filaOrdenada);
      setTurmas(turmasData);
      setAlunos(alunosData);
      setAutorizados(autData);
      setDashboard(dashData);
      
      // Gerar insights IA
      gerarInsightsIA(filaOrdenada, dashData);
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Sistema de prioridade IA
  const calcularPrioridadeIA = (filaItems) => {
    return filaItems.map(item => {
      let prioridade = 0;
      
      // Responsável já chegou (+30 pontos)
      if (item.autorizado_chegou) prioridade += 30;
      
      // Tempo de espera (+1 ponto por minuto)
      if (item.hora_entrada_fila) {
        const minutos = (Date.now() - new Date(item.hora_entrada_fila).getTime()) / 60000;
        prioridade += Math.min(minutos, 20);
      }
      
      // Tem irmãos na fila (+15 pontos)
      if (item.tem_irmaos) prioridade += 15;
      
      // Van escolar (+10 pontos - priorizar para não atrasar rota)
      if (item.van_id) prioridade += 10;
      
      // Já foi chamado mas não retirado (+5 pontos)
      if (item.status === 'chamado') prioridade += 5;
      
      return { ...item, prioridade_ia: prioridade };
    }).sort((a, b) => b.prioridade_ia - a.prioridade_ia);
  };

  // Gerar insights IA
  const gerarInsightsIA = (fila, dash) => {
    const insights = [];
    
    // Fila acima do normal
    if (fila.length > 15) {
      insights.push({
        tipo: 'alerta',
        icone: AlertTriangle,
        texto: `Fila acima do normal (${fila.length} alunos). Considere abrir mais pontos de saída.`,
        cor: 'text-amber-600'
      });
    }
    
    // Tempo médio alto
    if (dash?.tempo_medio_minutos > 5) {
      insights.push({
        tipo: 'aviso',
        icone: Clock,
        texto: `Tempo médio de ${dash.tempo_medio_minutos}min. Otimize o fluxo de saída.`,
        cor: 'text-blue-600'
      });
    }
    
    // Turmas com mais alunos aguardando
    const turmasCount = {};
    fila.forEach(item => {
      turmasCount[item.turma_nome] = (turmasCount[item.turma_nome] || 0) + 1;
    });
    const turmaMaisCheia = Object.entries(turmasCount).sort((a, b) => b[1] - a[1])[0];
    if (turmaMaisCheia && turmaMaisCheia[1] >= 3) {
      insights.push({
        tipo: 'sugestao',
        icone: Sparkles,
        texto: `Priorizar ${turmaMaisCheia[0]} (${turmaMaisCheia[1]} alunos aguardando)`,
        cor: 'text-purple-600'
      });
    }
    
    // Alunos aguardando há muito tempo
    const alunosEsperando = fila.filter(item => {
      if (!item.hora_entrada_fila) return false;
      const minutos = (Date.now() - new Date(item.hora_entrada_fila).getTime()) / 60000;
      return minutos > 10;
    });
    if (alunosEsperando.length > 0) {
      insights.push({
        tipo: 'urgente',
        icone: Zap,
        texto: `${alunosEsperando.length} aluno(s) aguardando há mais de 10 minutos`,
        cor: 'text-red-600'
      });
    }
    
    // Sucesso
    if (dash?.retiradas_hoje > 0 && dash?.tempo_medio_minutos < 3) {
      insights.push({
        tipo: 'sucesso',
        icone: TrendingUp,
        texto: `Excelente! Tempo médio abaixo de 3 min com ${dash.retiradas_hoje} retiradas.`,
        cor: 'text-green-600'
      });
    }
    
    setIaInsights(insights);
  };

  useEffect(() => {
    loadData();
    // Auto-refresh a cada 5 segundos
    refreshInterval.current = setInterval(loadData, 5000);
    return () => clearInterval(refreshInterval.current);
  }, [loadData]);

  const handleChamar = async (alunoId) => {
    try {
      await api.put(`/dnago/fila/${alunoId}/chamar`);
      toast.success('Aluno chamado!', { icon: '🔔' });
      // Efeito sonoro
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleigCAAAAAA==');
        audio.volume = 0.3;
        audio.play().catch(() => {});
      } catch (e) {}
      loadData();
    } catch (error) {
      toast.error('Erro ao chamar aluno');
    }
  };

  const handleDeslocamento = async (alunoId) => {
    try {
      await api.put(`/dnago/fila/${alunoId}/deslocamento`);
      toast.success('Aluno em deslocamento', { icon: '🚶' });
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleConfirmarRetirada = (aluno) => {
    const autorizadosDoAluno = autorizados.filter(a => 
      a.alunos_ids?.includes(aluno.aluno_id)
    );
    
    if (autorizadosDoAluno.length === 0) {
      toast.error('Nenhum autorizado cadastrado para este aluno');
      return;
    }
    
    if (autorizadosDoAluno.length === 1) {
      setSelectedAutorizado(autorizadosDoAluno[0]);
      setConfirmDialog({ open: true, aluno });
    } else {
      setSelectAutorizadoDialog({ open: true, aluno, autorizados: autorizadosDoAluno });
    }
  };

  const handleRetirada = async () => {
    if (!confirmDialog.aluno || !selectedAutorizado) return;
    
    try {
      await api.post('/dnago/retiradas', null, {
        params: {
          aluno_id: confirmDialog.aluno.aluno_id,
          autorizado_id: selectedAutorizado.id,
          tipo: 'normal'
        }
      });
      toast.success('Retirada confirmada!', { icon: '✅' });
      setConfirmDialog({ open: false, aluno: null });
      setSelectedAutorizado(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao confirmar retirada');
    }
  };

  const handleAddAlunoFila = async (alunoId) => {
    try {
      await api.post('/dnago/fila/adicionar', null, {
        params: { aluno_id: alunoId }
      });
      toast.success('Aluno adicionado à fila');
      setAddAlunoDialog(false);
      setSearchAluno('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar');
    }
  };

  const [removingIds, setRemovingIds] = useState(new Set());
  
  const handleRemoverFila = async (alunoId) => {
    if (removingIds.has(alunoId)) return;
    setRemovingIds(prev => new Set([...prev, alunoId]));
    try {
      await api.delete(`/dnago/fila/${alunoId}`);
      toast.success('Aluno removido da fila');
      // Atualizar UI imediatamente sem aguardar reload completo
      setFila(prev => prev.filter(i => i.aluno_id !== alunoId));
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao remover da fila');
    } finally {
      setRemovingIds(prev => { const n = new Set(prev); n.delete(alunoId); return n; });
    }
  };

  // Calcular tempo de espera
  const calcularTempoEspera = (horaEntrada) => {
    if (!horaEntrada) return null;
    const minutos = Math.floor((Date.now() - new Date(horaEntrada).getTime()) / 60000);
    if (minutos < 1) return 'Agora';
    if (minutos < 60) return `${minutos}min`;
    return `${Math.floor(minutos / 60)}h${minutos % 60}min`;
  };

  const filteredFila = fila.filter(item => {
    const matchSearch = item.aluno_nome?.toLowerCase().includes(search.toLowerCase());
    const matchTurma = filtroTurma === 'todas' || item.turma_id === filtroTurma;
    const matchStatus = filtroStatus === 'todos' || item.status === filtroStatus;
    return matchSearch && matchTurma && matchStatus;
  });

  const alunosNaoNaFila = alunos.filter(a => 
    !fila.some(f => f.aluno_id === a.id) &&
    a.name?.toLowerCase().includes(searchAluno.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto" />
          <p className="mt-4 text-gray-500">Carregando painel de saída...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header Premium */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-800 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
              <Shield className="w-8 h-8" />
              DNA GO - Painel de Saída
            </h1>
            <p className="text-purple-200 mt-1">
              Controle inteligente de retirada de alunos
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="secondary" 
              onClick={() => setAddAlunoDialog(true)}
              className="bg-white/20 hover:bg-white/30 text-white border-0"
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Aluno
            </Button>
            <Button 
              variant="secondary" 
              onClick={loadData}
              className="bg-white/20 hover:bg-white/30 text-white border-0"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Dashboard Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-200 text-sm">Fila Atual</p>
                <p className="text-4xl font-bold">{dashboard.fila_atual}</p>
              </div>
              <Users className="w-10 h-10 text-purple-300" />
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-200 text-sm">Retiradas Hoje</p>
                <p className="text-4xl font-bold">{dashboard.retiradas_hoje}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-300" />
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-200 text-sm">Tempo Médio</p>
                <p className="text-4xl font-bold">{dashboard.tempo_medio_minutos}<span className="text-xl">min</span></p>
              </div>
              <Clock className="w-10 h-10 text-blue-300" />
            </div>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-200 text-sm">Alertas</p>
                <p className="text-4xl font-bold">{dashboard.ocorrencias_abertas}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-amber-300" />
            </div>
          </div>
        </div>
      </div>

      {/* Painel IA */}
      {showIA && iaInsights.length > 0 && (
        <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-purple-700">
              <Brain className="w-4 h-4" />
              Insights da IA Operacional
              <Button 
                variant="ghost" 
                size="sm" 
                className="ml-auto h-6 text-xs"
                onClick={() => setShowIA(false)}
              >
                Ocultar
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-2">
              {iaInsights.map((insight, idx) => {
                const Icon = insight.icone;
                return (
                  <Badge 
                    key={idx} 
                    variant="outline" 
                    className={`${insight.cor} border-current py-1 px-3`}
                  >
                    <Icon className="w-3 h-3 mr-1" />
                    {insight.texto}
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar aluno..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filtroTurma} onValueChange={setFiltroTurma}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Todas as turmas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas as turmas</SelectItem>
                {turmas.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroStatus} onValueChange={setFiltroStatus}>
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="aguardando">Aguardando</SelectItem>
                <SelectItem value="chamado">Chamado</SelectItem>
                <SelectItem value="deslocamento">Deslocamento</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Grid de Alunos */}
      {filteredFila.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-xl text-gray-500">Nenhum aluno na fila de saída</p>
            <p className="text-sm text-gray-400 mt-2">
              Clique em "Adicionar Aluno" para começar
            </p>
            <Button 
              className="mt-4 bg-purple-600 hover:bg-purple-700"
              onClick={() => setAddAlunoDialog(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Aluno à Fila
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredFila.map((item, index) => {
            const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.aguardando;
            const StatusIcon = statusConfig.icon;
            const tempoEspera = calcularTempoEspera(item.hora_entrada_fila);
            
            return (
              <Card 
                key={item.aluno_id} 
                className={`relative overflow-hidden transition-all hover:shadow-xl border-l-4 ${statusConfig.borderColor} ${
                  item.status === 'chamado' ? 'ring-2 ring-blue-400 shadow-lg shadow-blue-100' : ''
                } ${item.prioridade_ia > 30 ? 'ring-1 ring-purple-300' : ''}`}
              >
                {/* Indicador de prioridade IA */}
                {item.prioridade_ia > 25 && (
                  <div className="absolute top-0 right-0 bg-purple-600 text-white text-[10px] px-2 py-0.5 rounded-bl-lg">
                    <Sparkles className="w-3 h-3 inline mr-1" />
                    Prioridade
                  </div>
                )}

                {/* Status Badge */}
                <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-medium text-white ${statusConfig.color}`}>
                  {statusConfig.label}
                </div>

                <CardContent className="p-4 pt-10">
                  {/* Foto e Info */}
                  <div className="flex items-start gap-3 mb-4">
                    <div className={`w-16 h-16 rounded-xl ${statusConfig.color} flex items-center justify-center overflow-hidden shrink-0 shadow-lg`}>
                      {item.aluno_foto ? (
                        <img src={item.aluno_foto} alt="" className="w-16 h-16 object-cover" />
                      ) : (
                        <span className="text-2xl text-white font-bold">
                          {item.aluno_nome?.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-bold text-gray-900 truncate text-lg">{item.aluno_nome}</h3>
                      <p className="text-sm text-gray-500">{item.turma_nome}</p>
                      {tempoEspera && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                          <Clock className="w-3 h-3" />
                          {tempoEspera}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Info adicional */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {item.autorizado_chegou && item.autorizado_nome && (
                      <Badge variant="secondary" className="text-[10px] bg-green-100 text-green-700">
                        <UserCheck className="w-3 h-3 mr-1" />
                        {item.autorizado_nome}
                      </Badge>
                    )}
                    {item.tem_irmaos && (
                      <Badge variant="secondary" className="text-[10px]">
                        <Users className="w-3 h-3 mr-1" />
                        Irmãos
                      </Badge>
                    )}
                    {item.van_id && (
                      <Badge variant="secondary" className="text-[10px] bg-blue-100 text-blue-700">
                        🚐 Van
                      </Badge>
                    )}
                  </div>

                  {/* Botões de Ação */}
                  <div className="flex gap-2">
                    {item.status === 'aguardando' && (
                      <Button 
                        className="flex-1 bg-blue-600 hover:bg-blue-700 h-12 text-base font-semibold shadow-lg"
                        onClick={() => handleChamar(item.aluno_id)}
                      >
                        <Volume2 className="w-5 h-5 mr-2" />
                        CHAMAR
                      </Button>
                    )}
                    
                    {item.status === 'chamado' && (
                      <>
                        <Button 
                          className="flex-1 bg-purple-600 hover:bg-purple-700 h-12"
                          onClick={() => handleDeslocamento(item.aluno_id)}
                        >
                          <ArrowRight className="w-5 h-5 mr-1" />
                          DESLOC.
                        </Button>
                        {/* Botão WhatsApp */}
                        {item.responsavel_id && (
                          <WhatsAppButton 
                            alunoId={item.aluno_id}
                            responsavelId={item.responsavel_id}
                            alunoNome={item.aluno_nome}
                            compact={true}
                          />
                        )}
                      </>
                    )}
                    
                    {(item.status === 'chamado' || item.status === 'deslocamento') && (
                      <Button 
                        className="flex-1 bg-green-600 hover:bg-green-700 h-12 text-base font-semibold shadow-lg"
                        onClick={() => handleConfirmarRetirada(item)}
                      >
                        <CheckCircle className="w-5 h-5 mr-2" />
                        OK
                      </Button>
                    )}

                    <Button 
                      variant="outline"
                      className="px-3 h-12"
                      disabled={removingIds.has(item.aluno_id)}
                      onClick={() => handleRemoverFila(item.aluno_id)}
                    >
                      {removingIds.has(item.aluno_id) ? '...' : '✕'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Dialog Adicionar Aluno */}
      <Dialog open={addAlunoDialog} onOpenChange={setAddAlunoDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-purple-600" />
              Adicionar Aluno à Fila
            </DialogTitle>
            <DialogDescription>
              Busque e selecione o aluno para adicionar à fila de saída
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar aluno por nome..."
                value={searchAluno}
                onChange={(e) => setSearchAluno(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="max-h-64 overflow-y-auto space-y-2">
              {alunosNaoNaFila.slice(0, 20).map(aluno => (
                <Button
                  key={aluno.id}
                  variant="outline"
                  className="w-full justify-start gap-3 h-auto p-3"
                  onClick={() => handleAddAlunoFila(aluno.id)}
                >
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                    <img src={aluno.photo_url || "/default-avatar.png"} onError={(e) => { e.currentTarget.src = "/default-avatar.png"; }} alt="" className="w-10 h-10 rounded-full object-cover" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium">{aluno.name}</p>
                    <p className="text-xs text-gray-500">{aluno.class_name || 'Sem turma'}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 ml-auto text-gray-400" />
                </Button>
              ))}
              {alunosNaoNaFila.length === 0 && searchAluno && (
                <p className="text-center text-gray-500 py-4">
                  Nenhum aluno encontrado
                </p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Seleção de Autorizado */}
      <Dialog 
        open={selectAutorizadoDialog.open} 
        onOpenChange={(open) => !open && setSelectAutorizadoDialog({ open: false, aluno: null, autorizados: [] })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Selecione o Autorizado</DialogTitle>
            <DialogDescription>
              Quem está retirando {selectAutorizadoDialog.aluno?.aluno_nome}?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {selectAutorizadoDialog.autorizados?.map((auth) => (
              <Button
                key={auth.id}
                variant="outline"
                className="w-full justify-start gap-3 h-auto p-3"
                onClick={() => {
                  setSelectedAutorizado(auth);
                  setSelectAutorizadoDialog({ open: false, aluno: null, autorizados: [] });
                  setConfirmDialog({ open: true, aluno: selectAutorizadoDialog.aluno });
                }}
              >
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  {auth.foto_url ? (
                    <img src={auth.foto_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <span className="text-xl text-purple-600 font-semibold">
                      {auth.nome?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="text-left">
                  <p className="font-medium">{auth.nome}</p>
                  <p className="text-xs text-gray-500 capitalize">{auth.grau_parentesco}</p>
                </div>
                {auth.face_template && (
                  <Badge className="ml-auto bg-green-100 text-green-700">
                    <Camera className="w-3 h-3 mr-1" />
                    Face
                  </Badge>
                )}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmação de Retirada */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, aluno: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-6 h-6" />
              Confirmar Retirada
            </DialogTitle>
          </DialogHeader>
          
          {confirmDialog.aluno && selectedAutorizado && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-500 mb-1">Aluno</p>
                <p className="font-bold text-xl">{confirmDialog.aluno.aluno_nome}</p>
                <p className="text-sm text-gray-500">{confirmDialog.aluno.turma_nome}</p>
              </div>
              
              <div className="p-4 bg-green-50 rounded-xl border-2 border-green-200">
                <p className="text-sm text-green-600 mb-1">Sendo retirado por</p>
                <p className="font-bold text-xl text-green-700">{selectedAutorizado.nome}</p>
                <p className="text-sm text-green-600 capitalize">{selectedAutorizado.grau_parentesco}</p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmDialog({ open: false, aluno: null })}>
              Cancelar
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700 h-12 px-8 text-lg" 
              onClick={handleRetirada}
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              Confirmar Retirada
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DN-AI Widget */}
      <DnaGoDnAiWidget />
    </div>
  );
}
